/**
 * What a page looks like to the rail, and how the rail groups a list of them.
 *
 * The list itself is not here. It lives in `src/routes/-nav.ts`, beside the
 * pages it names, so adding a page touches the routes tree and not the
 * framework. This module is framework: it holds only the shape and the two
 * pure functions the rail reads it with.
 */
import type { IconName } from '../design-system/icon.tsx'

export interface InternalPage {
  id: string
  path: string
  title: string
  /** Search terms supported by the page content, including common abbreviations. */
  keywords?: readonly string[]
  /** A shorter title for section rows in the rail. Other surfaces use title. */
  railTitle?: string
  /**
   * One sentence: what the reader sees, and the question the page answers.
   *
   * Required, so a page cannot be declared without one. The landing page
   * prints it under the title, and page search matches it.
   */
  description: string
  /**
   * Groups this page under a named category of the rail — "CS Tools", say.
   * Two pages sharing a group name render in one category. Selecting that
   * category opens those reports beside the rail, in the order they appear
   * in this list.
   *
   * **Optional in this type, required by the console's rail.** The field
   * stays optional because `groupInternalPages` still answers for a caller
   * with flat rows, but `src/routes/__root.tsx` reads the sections alone: a
   * page here with no group reaches its URL and appears nowhere in the rail.
   * `src/routes/-nav.test.ts` fails on one, which is what makes the omission
   * a red gate rather than a missing row somebody notices later.
   */
  group?: string
  /**
   * False keeps this page out of the rail. Absent means true.
   *
   * For a page that opens only from a link, because it needs an id the rail
   * cannot supply. The page is still declared, so `-nav.test.ts` still knows
   * every route file is there on purpose.
   */
  inRail?: boolean
  /**
   * True when this page's table grammar reads `orgIds`.
   *
   * Page search offers "filter this report" only on these pages. The parameter
   * sticks only when the grammar declares it. `src/routes/-nav.test.ts` checks
   * the flag against those grammars.
   */
  takesMerchants?: boolean
  /** True when this page reads inclusive civil days from `from` and `to`. */
  takesDateRange?: boolean
  /**
   * True shows this page in the fixed Tools foot, above the account row.
   * Absent means false.
   *
   * The foot does not scroll away and does not collapse. The landing page
   * does not list it. The page keeps its group, so one app folder still has
   * one group name.
   */
  pinned?: boolean
  /** An explicit row glyph. Only pinned pages receive a default glyph. */
  icon?: IconName
  /** The page fills the whole TV: no header, no padding, no letterbox. */
  tvFullscreen?: boolean
}

/** One named section of the rail: a group's title, and the pages under it, in declared order. */
export interface InternalNavSection {
  group: string
  pages: InternalPage[]
}

/**
 * Apply a saved section order.
 *
 * A name that matches no section, including a saved Tools entry, has no
 * effect. A section the saved order does not name stays visible.
 *
 * @param sections - The sections to order.
 * @param order - The saved group names.
 * @returns The sections in saved order.
 */
export function orderNavSections(
  sections: readonly InternalNavSection[],
  order: readonly string[]
): InternalNavSection[] {
  const rank = new Map(order.map((group, index) => [group, index]))
  const sorted = sections.map((section, index) => ({ section, index }))
  sorted.sort(
    (a, b) =>
      (rank.get(a.section.group) ?? order.length + a.index) -
      (rank.get(b.section.group) ?? order.length + b.index)
  )
  return sorted.map(item => item.section)
}

/**
 * Move one section by one place.
 *
 * A saved name that matches no section stays in the list. It does not add a
 * section, and it does not change the sections that do exist.
 *
 * @param sections - The sections in their current order.
 * @param group - The group to move.
 * @param direction - One place up or down.
 * @param savedOrder - The order already stored, which may name unknown groups.
 * @returns The group names to store.
 */
export function moveNavSection(
  sections: readonly InternalNavSection[],
  group: string,
  direction: -1 | 1,
  savedOrder: readonly string[] = []
): string[] {
  const names = sections.map(section => section.group)
  const from = names.indexOf(group)
  const to = from + direction
  if (from < 0 || to < 0 || to >= names.length)
    return savedOrder.length ? [...savedOrder] : names
  ;[names[from], names[to]] = [names[to], names[from]]
  const visible = new Set(names)
  let next = 0
  const merged = [...new Set(savedOrder)].map(name =>
    visible.has(name) ? names[next++] : name
  )
  return [...merged, ...names.slice(next)]
}

/**
 * Split the declared page list into the rows a nav renders flat and the
 * sections it renders as categories.
 *
 * Grouped and returned in one pass, in declaration order, rather than with a
 * second sort afterwards — `pages.ts` declares the whole rail's order
 * already, and a nav that regrouped it would only need to undo a sort this
 * function never did.
 *
 * @param pages - The declared page list, or a subset of it in tests.
 * @returns The flat rows, and the grouped sections, both in declared order.
 */
export function groupInternalPages(pages: readonly InternalPage[]): {
  ungrouped: InternalPage[]
  sections: InternalNavSection[]
} {
  const ungrouped: InternalPage[] = []
  const sections: InternalNavSection[] = []
  const byGroup = new Map<string, InternalPage[]>()

  for (const page of pages) {
    if (!page.group) {
      ungrouped.push(page)
      continue
    }
    const existing = byGroup.get(page.group)
    if (existing) {
      existing.push(page)
      continue
    }
    const created = [page]
    byGroup.set(page.group, created)
    sections.push({ group: page.group, pages: created })
  }

  return { ungrouped, sections }
}

/**
 * The pages the rail groups into sections: every declared page but a
 * link-only one or a pinned one.
 *
 * @param pages - The declared page list.
 * @returns The pages to group into the rail, in declared order.
 */
export function railPages(pages: readonly InternalPage[]): InternalPage[] {
  return pages.filter(page => page.inRail !== false && page.pinned !== true)
}

/**
 * The pages the rail pins above the account row.
 *
 * @param pages - The declared page list.
 * @returns The pinned pages, in declared order.
 */
export function pinnedPages(pages: readonly InternalPage[]): InternalPage[] {
  return pages.filter(page => page.pinned === true && page.inRail !== false)
}

/**
 * The tool links the fixed foot shows.
 *
 * While access is absent — it is still loading, or the check failed — every
 * pinned tool is listed. When access answers, only tools whose ids are in
 * `pageIds` are listed. This is display only. Each page still checks access.
 *
 * @param pinned - The pinned pages, in declared order.
 * @param access - The access answer, or undefined while that answer is absent.
 * @returns The foot links, in declared order.
 */
export function footPages(
  pinned: readonly InternalPage[],
  access: { readonly pageIds: readonly string[] } | undefined
): InternalPage[] {
  if (access === undefined) return [...pinned]
  const allowed = new Set(access.pageIds)
  return pinned.filter(page => allowed.has(page.id))
}

/**
 * Compare two route paths without tripping over a trailing slash or a query.
 *
 * The rail marks the active row with this, so `/ach/returns/` and
 * `/ach/returns?window=7` must both match the declared path.
 *
 * @param pathname - The path to normalize.
 * @returns The path without its query string or trailing slash.
 */
export function normalizePath(pathname: string): string {
  const path = pathname.split('?')[0]

  return path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path
}

/** Find the most specific declared page for a route or a nested route. */
export function pageForPath(
  pages: readonly InternalPage[],
  pathname: string
): InternalPage | undefined {
  const path = normalizePath(pathname)
  let matched: InternalPage | undefined
  for (const page of pages) {
    const declared = normalizePath(page.path)
    if (
      (path === declared || path.startsWith(`${declared}/`)) &&
      (!matched || declared.length > normalizePath(matched.path).length)
    ) {
      matched = page
    }
  }
  return matched
}

/**
 * Whether the page at a path plays full screen on a TV.
 *
 * @param pages - The declared page list.
 * @param pathname - The path of the page that is up, or undefined for none.
 * @returns True when the matching page declares `tvFullscreen`.
 */
export function isTvFullscreenPath(
  pages: readonly InternalPage[],
  pathname: string | undefined
): boolean {
  return (
    pathname !== undefined &&
    pageForPath(pages, pathname)?.tvFullscreen === true
  )
}

/** How many pages Recent keeps. Newest first. */
export const RECENT_PAGE_LIMIT = 5

/** How many pages a reader can star. */
export const STARRED_PAGE_LIMIT = 50

/**
 * Move one page to the front of Recent and keep the cap.
 *
 * A repeat is dropped, so the list holds each id once. The input is not changed.
 *
 * @param recent - The ids held now, newest first.
 * @param pageId - The page the reader just opened.
 * @returns The new list.
 */
export function withRecentPage(
  recent: readonly string[],
  pageId: string
): string[] {
  return [pageId, ...recent.filter(id => id !== pageId)].slice(
    0,
    RECENT_PAGE_LIMIT
  )
}

/**
 * Star a page, or unstar it when it is already starred.
 *
 * A new star goes at the end. Unstarring leaves the other ids in place.
 * A full list first drops the ids the rail does not show, because the reader
 * cannot unstar those. A list still full returns the same array, so the
 * caller can skip a save the backend would refuse.
 *
 * @param starred - The ids held now, in star order.
 * @param pageId - The page the reader flipped.
 * @param shownIds - The starred ids the rail shows.
 * @returns The new list, or the same list when the star would pass the cap.
 */
export function toggleStarred(
  starred: readonly string[],
  pageId: string,
  shownIds: readonly string[]
): readonly string[] {
  if (starred.includes(pageId)) {
    return starred.filter(id => id !== pageId)
  }
  const kept =
    starred.length < STARRED_PAGE_LIMIT
      ? starred
      : starred.filter(id => shownIds.includes(id))
  if (kept.length >= STARRED_PAGE_LIMIT) {
    return starred
  }

  return [...kept, pageId]
}

/**
 * The pages a stored id list still shows, in that stored order.
 *
 * An unknown id, a repeated id, a page with `inRail: false`, and a page the
 * reader may not open are skipped. They stay in the stored list.
 *
 * @param catalog - Every declared page.
 * @param ids - The stored ids, in the order to show.
 * @param allowedIds - The page ids the backend allows.
 * @returns The pages to render.
 */
export function pagesInStoredOrder(
  catalog: readonly InternalPage[],
  ids: readonly string[],
  allowedIds: readonly string[]
): InternalPage[] {
  const byId = new Map(catalog.map(page => [page.id, page]))
  const allowed = new Set(allowedIds)
  const seen = new Set<string>()
  const pages: InternalPage[] = []
  for (const id of ids) {
    if (seen.has(id)) continue
    seen.add(id)
    const page = byId.get(id)
    if (page === undefined || page.inRail === false || !allowed.has(id)) {
      continue
    }
    pages.push(page)
  }

  return pages
}

/**
 * The sections the backend allows this viewer, before the reader's own choices.
 *
 * A section left with no page is dropped.
 *
 * @param sections - Every declared section.
 * @param pageIds - The page ids the backend allows.
 * @returns The sections to offer this viewer, in declared order.
 */
export function accessibleNavSections(
  sections: readonly InternalNavSection[],
  pageIds: readonly string[]
): InternalNavSection[] {
  const allowed = new Set(pageIds)
  return sections
    .map(section => ({
      group: section.group,
      pages: section.pages.filter(page => allowed.has(page.id))
    }))
    .filter(section => section.pages.length > 0)
}
